import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, Building2Icon, CheckIcon, DropletsIcon, MessageSquareIcon, SproutIcon, WrenchIcon, BoxIcon } from "lucide-react";
import { SITE } from "../constants/site";
type Service = {
  icon: BoxIcon;
  title: string;
  label: string;
  description: string;
  audience: string;
  highlights: string[];
};
const SERVICES: Service[] = [{
  icon: WrenchIcon,
  label: '01 / Repair',
  title: 'Sprinkler Repair & Diagnostics',
  description: 'Broken heads, clogged nozzles, pressure problems, leaks — we find the cause, not just the symptom.',
  audience: 'For systems that need answers before they need another quick fix.',
  highlights: ['Leak detection', 'Pressure correction', 'Head & nozzle repair']
}, {
  icon: DropletsIcon,
  label: '02 / Install',
  title: 'New Irrigation System Installation',
  description: 'Custom-designed irrigation for your property, not a cookie-cutter layout copied from the last job.',
  audience: 'Residential and commercial systems engineered for even, dependable coverage.',
  highlights: ['Custom zone design', 'Code-compliant install', 'Residential & commercial']
}, {
  icon: SproutIcon,
  label: '03 / Lawn',
  title: 'Sod & Grass Installation',
  description: 'New sod needs the right irrigation beneath it. We coordinate both for a lawn built to take root and thrive.',
  audience: 'One team for the system underneath and the lawn you see every day.',
  highlights: ['Sod installation', 'Irrigation coordination', 'Coverage planning']
}, {
  icon: Building2Icon,
  label: '04 / Commercial',
  title: 'Commercial Irrigation Services',
  description: 'Licensed oversight, documentation, and reliable zone management for high-visibility commercial properties.',
  audience: 'Built for property managers, HOAs, retail centers, and office parks.',
  highlights: ['HOA & property management', 'System documentation', 'Licensed oversight']
}];
export function ServicesSection() {
  const [selectedService, setSelectedService] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const activeService = SERVICES[selectedService];
  const ActiveIcon = activeService.icon;
  return <section id="services" className="w-full bg-slate-50 py-16 sm:py-24" data-id="element-409" data-mp="3s33djv46oklv">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-410" data-mp="vqwdg3aw75y">
        <div className="mx-auto max-w-3xl text-center" data-id="element-411" data-mp="xbq53hsjm2r">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-dark" data-id="element-412" data-mp="2zeelsmp0prms">
            What We Do
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-slate-950 sm:text-4xl" data-id="element-413" data-mp="34eah7xjngaqq">
            One team. Every irrigation need.
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-414" data-mp="3ebf2lv5o826h">
            Select a service to see how Northface protects your lawn, water use, and property.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(6,58,110,0.10)]" data-id="element-415" data-mp="tjn21aaul9ev">
          <div className="grid lg:grid-cols-[0.82fr_1.18fr]" data-id="element-416" data-mp="2e1nt99rqmi88">
            <div className="bg-slate-950 p-5 sm:p-7 lg:p-8" data-id="element-417" data-mp="292kozrjl0g1b">
              <div className="flex items-center justify-between border-b border-white/10 pb-5" data-id="element-418" data-mp="295736a0z9tsi">
                <div data-id="element-419" data-mp="24ab0mfy47o15">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-light" data-id="element-420" data-mp="1zdkmhy5vf2id">
                    Service menu
                  </p>
                  <p className="mt-1 text-sm text-slate-400" data-id="element-421" data-mp="1ufrfx40no6hk">Choose what you need help with.</p>
                </div>
                <span className="font-display text-lg font-extrabold text-white" data-id="element-422" data-mp="1qepfwkywzqej">0{selectedService + 1}/04</span>
              </div>

              <div className="mt-4 space-y-2" role="tablist" aria-label="Irrigation services" data-id="element-423" data-mp="1geotgq5r0u64">
                {SERVICES.map((service, index) => {
                const isSelected = selectedService === index;
                const Icon = service.icon;
                return <motion.button key={service.title} type="button" role="tab" aria-selected={isSelected} whileHover={shouldReduceMotion ? undefined : {
                  x: 3
                }} transition={{
                  duration: 0.16,
                  ease: [0.22, 1, 0.36, 1]
                }} onClick={() => setSelectedService(index)} className={`flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-nfgreen focus:ring-offset-2 focus:ring-offset-slate-950 ${isSelected ? 'bg-white text-slate-950 shadow-lg' : 'text-white hover:bg-white/10'}`} data-id="element-424" data-mp="3at9cyzic2jnx" data-mpi={index}>
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isSelected ? 'bg-nfblue text-white' : 'bg-white/10 text-nfgreen-light'}`} data-id="element-425" data-mp="1fqcv7k1r5wla">
                        <Icon className="h-5 w-5" data-id="element-426" data-mp="1knd2fyac95dz" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-427" data-mp="15sqbmk9idguz">
                        <span className={`block text-[11px] font-bold uppercase tracking-[0.12em] ${isSelected ? 'text-nfgreen-dark' : 'text-slate-400'}`} data-id="element-428" data-mp="1art7be3lmaqa">
                          {service.label}
                        </span>
                        <span className="mt-1 block font-display text-base font-bold leading-tight" data-id="element-429" data-mp="n57q5uldjyyc">
                          {service.title}
                        </span>
                      </span>
                      <ArrowRightIcon className={`h-4 w-4 shrink-0 transition-transform ${isSelected ? 'translate-x-0 text-nfblue' : '-translate-x-1 text-slate-500'}`} data-id="element-430" data-mp="w3t8bomxpbyl" />
                    </motion.button>;
              })}
              </div>
            </div>

            <div className="relative overflow-hidden p-7 sm:p-10 lg:p-12" data-id="element-431" data-mp="1jtvtrlwcfjo8">
              <div className="absolute right-0 top-0 h-52 w-52 rounded-bl-full bg-nfgreen/10" data-id="element-432" data-mp="1jvzxa2qbygnz" />
              <div className="relative min-h-[370px]" data-id="element-433" data-mp="1ex5daxgmmm2a">
                <AnimatePresence mode="wait" initial={false} data-id="element-434" data-mp="1ttebdvos6td4">
                  <motion.div key={activeService.title} role="tabpanel" initial={shouldReduceMotion ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  y: 14
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={shouldReduceMotion ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.24,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-435" data-mp="1ot8e9zzqsenn">
                    <div className="flex items-start justify-between gap-5" data-id="element-436" data-mp="2fmicwlz0j93t">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-nfblue text-white shadow-lg shadow-nfblue/20" data-id="element-437" data-mp="2jkg3nt8fm39b">
                        <ActiveIcon className="h-8 w-8" data-id="element-438" data-mp="2tkqhng2ajepj" />
                      </div>
                      <span className="rounded-full border border-nfgreen/20 bg-nfgreen/10 px-3 py-1.5 text-xs font-bold text-nfgreen-dark" data-id="element-439" data-mp="u72wlw8xq14i">
                        Licensed service
                      </span>
                    </div>

                    <p className="mt-8 text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-dark" data-id="element-440" data-mp="rnq06rr3t475">
                      {activeService.label}
                    </p>
                    <h3 className="mt-3 max-w-xl font-display text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl" data-id="element-441" data-mp="hwlk1opozdaq">
                      {activeService.title}
                    </h3>
                    <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600" data-id="element-442" data-mp="1al91183ergih">
                      {activeService.description}
                    </p>

                    <div className="mt-7 rounded-2xl border border-slate-100 bg-slate-50 p-5" data-id="element-443" data-mp="15kcxwwqvrye4">
                      <p className="text-sm font-semibold leading-relaxed text-slate-700" data-id="element-444" data-mp="10li99e68ffm3">
                        {activeService.audience}
                      </p>
                      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-445" data-mp="3lemyigpnzm9v">
                        {activeService.highlights.map(item => <li key={item} className="flex items-center gap-2 text-sm font-bold text-slate-800" data-id="element-446" data-mp="3gdpmklv8zogt">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-nfgreen/15 text-nfgreen-dark" data-id="element-447" data-mp="9vnrl8gwtkgb">
                              <CheckIcon className="h-3.5 w-3.5" data-id="element-448" data-mp="wtulgzo2s28" />
                            </span>
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-449" data-mp="snggorsuylir">
                      <a href={SITE.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full bg-nfblue px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-nfblue/20 transition-colors hover:bg-nfblue-dark focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" data-id="element-450" data-mp="xnckapxn8axs">
                        Schedule a diagnostic
                        <ArrowRightIcon className="h-4 w-4" data-id="element-451" data-mp="1blavjmgo3r92" />
                      </a>
                      <a href={SITE.smsHref} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-nfblue transition-colors hover:bg-nfblue/5 focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" data-id="element-452" data-mp="1gevmwvnr9mza">
                        <MessageSquareIcon className="h-4 w-4" data-id="element-453" data-mp="3atspd8lxhw0w" />
                        Ask a service question
                      </a>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}